import React, { useEffect, useState, useRef } from 'react';
import {Fieldset, Group, TextInput, Button, FocusTrap, TagsInput, Text, Flex, Paper, Box} from '@mantine/core';
import { IconLoader2, IconMapPin } from '@tabler/icons-react';
import { MonthPickerInput, DatePickerInput } from '@mantine/dates';
import { DateFormatControl } from '../sortImages/DateFormatToggle';
import { ImageData } from '../../types/Image';
import { EventData, DateFormat } from '../../types/Event';
import { ShoeboxLocation } from '../../types/Location';
import { loadGoogleMapsScript } from '../../utils/locationUtils';
import ImageWithSkeleton from '../ImageWithSkeleton';

// Simplified interface
interface EventDetailsFormProps {
    eventData: EventData;
    onEventUpdate: (event: Partial<EventData>) => void;
}

/**
 * A reusable component for displaying and editing event details
 */
export const EventDetailsForm: React.FC<EventDetailsFormProps> = ({
                                                                      eventData,
                                                                      onEventUpdate
                                                                  }) => {
    // Form state
    const [date, setDate] = useState<Date | null>(null);
    const [format, setFormat] = useState<DateFormat>(DateFormat.DAY);
    const [description, setDescription] = useState<string>(eventData.title || '');
    const [locationDescription, setLocationDescription] = useState<string>(eventData.location_description || '');
    const [location, setLocation] = useState<ShoeboxLocation | null>(eventData.location || null);
    const [selectedPlaceName, setSelectedPlaceName] = useState<string>(eventData.location?.sublocation || '');
    const [selectedPlaceAddress, setSelectedPlaceAddress] = useState<string>(eventData.location?.address || '');
    const [tags, setTags] = useState<string[]>(eventData.keywords || []);

    // Google Maps autocomplete state and refs
    const [isGoogleMapsLoading, setIsGoogleMapsLoading] = useState(false);
    const [googleMapsError, setGoogleMapsError] = useState<string | null>(null);
    const locationSearchContainerRef = useRef<HTMLDivElement>(null);
    const googleMapsLoadedRef = useRef(false);
    const placeAutocompleteRef = useRef<any>(null);

    // Initialize form with event data
    useEffect(() => {
        if (eventData) {
            if (eventData.date) {
                setDate(eventData.date instanceof Date ? eventData.date : new Date(eventData.date));
            }
            if (eventData.dateFormat) {
                setFormat(eventData.dateFormat);
            }
            if (eventData.title) {
                setDescription(eventData.title);
            }
            if (eventData.location_description) {
                setLocationDescription(eventData.location_description);
            }
            if (eventData.location) {
                setLocation(eventData.location);
                // Display the address or city in the location field
                if (eventData.location.address) {
                    setSelectedPlaceName(eventData.location.address);
                } else if (eventData.location.city) {
                    setSelectedPlaceName(eventData.location.city);
                }
            }
            if (eventData.keywords) {
                setTags(eventData.keywords);
            }
        }
    }, [eventData]);

    // Initialize Google Places Autocomplete when component mounts
    useEffect(() => {
        // Load Google Maps API if not already loaded
        if (!googleMapsLoadedRef.current) {
            setIsGoogleMapsLoading(true);
            setGoogleMapsError(null);

            // Add a small delay before loading to ensure DOM is ready
            setTimeout(() => {
                loadGoogleMapsScript()
                    .then(() => {
                        googleMapsLoadedRef.current = true;
                        setIsGoogleMapsLoading(false);

                        // Add a small delay to ensure the API is fully initialized
                        setTimeout(() => {
                            if (locationSearchContainerRef.current) {
                                initGooglePlacesAutocomplete();
                            }
                        }, 100);
                    })
                    .catch(error => {
                        console.error('Failed to load Google Maps API:', error);
                        setGoogleMapsError('Failed to load Google Maps API');
                        setIsGoogleMapsLoading(false);
                    });
            }, 100);
        } else if (locationSearchContainerRef.current) {
            // If API is already loaded, just initialize the autocomplete
            initGooglePlacesAutocomplete();
        }
    }, []);

    // Initialize Google Places Autocomplete
    const initGooglePlacesAutocomplete = () => {
        if (!locationSearchContainerRef.current) {
            console.error('Location search container ref not available');
            return;
        }

        if (!window.google || !window.google.maps || !window.google.maps.places) {
            console.error('Google Maps API not loaded properly');
            setGoogleMapsError('Google Maps API not loaded properly');
            return;
        }

        try {
            // Clear the container first
            locationSearchContainerRef.current.innerHTML = '';

            // Create a wrapper div for the input and icon
            const wrapperDiv = document.createElement('div');
            wrapperDiv.style.position = 'relative';
            wrapperDiv.style.width = '100%';

            // Create the magnifying glass icon
            const searchIcon = document.createElement('div');
            // Use a simple SVG for the search icon with the dimmed color
            searchIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#868e96" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>`;
            searchIcon.style.position = 'absolute';
            searchIcon.style.left = '12px';
            searchIcon.style.top = '50%';
            searchIcon.style.transform = 'translateY(-50%)';
            searchIcon.style.pointerEvents = 'none';
            searchIcon.style.zIndex = '1';

            // Create a simple input element for the autocomplete
            const inputElement = document.createElement('input');
            inputElement.id = 'location-search';
            inputElement.type = 'text';
            inputElement.placeholder = 'Search for a location';
            inputElement.style.width = '100%';
            inputElement.style.padding = '10px 12px'; // Match Mantine md input padding
            inputElement.style.paddingLeft = '36px'; // Make room for the icon
            inputElement.style.boxSizing = 'border-box';
            inputElement.style.border = '1px solid transparent';
            inputElement.style.borderRadius = '4px';
            inputElement.style.outline = 'none';
            inputElement.style.fontSize = '16px'; // Mantine md size is typically 16px
            inputElement.style.lineHeight = '1.5';
            inputElement.style.backgroundColor = 'white';

            // Set placeholder color to match Mantine's dimmed text
            inputElement.style.color = '#000';
            // Apply the placeholder style using the ::placeholder pseudo-element
            const style = document.createElement('style');
            style.innerHTML = `
                #location-search::placeholder {
                    color: #868e96; /* Mantine's dimmed text color */
                    opacity: 1;
                }
            `;
            document.head.appendChild(style);

            // If we already have a selected place name, show it in the input
            if (selectedPlaceName) {
                inputElement.value = selectedPlaceName;
            }

            // Add the elements to the wrapper and the wrapper to the container
            wrapperDiv.appendChild(searchIcon);
            wrapperDiv.appendChild(inputElement);
            locationSearchContainerRef.current.appendChild(wrapperDiv);
            // Initialize the autocomplete on the input
            const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
                types: ['geocode', 'establishment'],
                fields: ['address_components', 'geometry', 'name', 'formatted_address', 'place_id']
            });

            // Add listener for place selection
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place && place.formatted_address) {
                    handlePlaceSelection(place);
                    // Update the input field with the place name
                    inputElement.value = place.name || place.formatted_address;
                } else {
                    console.warn('Place selected but no formatted address found:', place);
                }
            });

            // Store reference for cleanup
            placeAutocompleteRef.current = autocomplete;

            // Clear any previous error
            setGoogleMapsError(null);
        } catch (error) {
            console.error('Error initializing Google Places Autocomplete:', error);
            setGoogleMapsError('Error initializing location search');
        }
    };

    /**
     * Handle place selection from Google Places Autocomplete
     * Creates a comprehensive ShoeboxLocation object from the selected place data
     * @param place - The place object returned by Google Places API
     */
    const handlePlaceSelection = (place: any) => {
        if (!place) return;
        
        // Create a ShoeboxLocation object from the place data
        const newLocation: ShoeboxLocation = {
            address: place.formatted_address,
            sublocation: place.name || undefined
        };

        // Extract address components
        if (place.address_components) {
            let streetNumber = '';
            let route = '';
            
            for (const component of place.address_components) {
                const types = component.types;

                if (types.includes('locality')) {
                    newLocation.city = component.long_name;
                } else if (types.includes('administrative_area_level_1')) {
                    newLocation.state = component.long_name;
                } else if (types.includes('country')) {
                    newLocation.country = component.long_name;
                } else if (types.includes('route')) {
                    route = component.long_name;
                } else if (types.includes('street_number')) {
                    streetNumber = component.long_name;
                }
            }
            
            // Combine street number and route for complete street address
            if (streetNumber || route) {
                newLocation.street_address = [streetNumber, route].filter(Boolean).join(' ');
            }
        }

        // Add geolocation if available
        if (place.geometry?.location) {
            newLocation.geolocation = {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng()
            };
            
            // Generate static map URL using Google Maps Static API
            // Note: In production, you would need to add your API key
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            newLocation.static_map_url = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=YOUR_API_KEY`;
        }
        
        // Add external identifiers
        if (place.place_id) {
            newLocation.external_identifiers = [
                {
                    provider: 'google_maps',
                    id: place.place_id,
                    url: `https://www.google.com/maps/place/?q=place_id:${place.place_id}`
                }
            ];
        }

        // Set the location object
        setLocation(newLocation);

        // Set the display name and address separately
        setSelectedPlaceName(place.name || '');
        setSelectedPlaceAddress(place.formatted_address || '');

        onEventUpdate({location: newLocation});
    };

    // Determine which images to display
    const imagesToDisplay = eventData?.images || [];

    return (
        <>
            {imagesToDisplay.length > 0 && (
                <Paper shadow="sm" radius="lg" withBorder p="md" style={{ backgroundColor: '#f0f0f0' }}>
                    <Text fw={500}>Images Included in Event:</Text>
                    <Flex
                        mt="xs"
                        direction="row"
                        wrap="wrap"
                        gap={{ base: '3px', sm: '3px' }}
                        justify={imagesToDisplay.length < 4 ? 'center' : 'flex-start'}
                    >
                        {imagesToDisplay.slice(0, 3).map(image => (
                            <div
                                key={image.key}
                                style={{
                                    position: 'relative',
                                    width: 'calc((100% - 9px) / 4)',
                                    aspectRatio: 1 / 1
                                }}
                            >
                                <ImageWithSkeleton
                                    src={image.thumbnail_url || ''}
                                    alt="Thumbnail"
                                    onClick={() => {}}
                                />
                            </div>
                        ))}
                        {imagesToDisplay.length > 3 && (
                            <div
                                style={{
                                    position: 'relative',
                                    width: 'calc((100% - 9px) / 4)',
                                    aspectRatio: 1 / 1
                                }}
                            >
                                <ImageWithSkeleton
                                    src={imagesToDisplay[3]?.thumbnail_url || ''}
                                    alt="Thumbnail"
                                    onClick={() => {}}
                                />
                                {imagesToDisplay.length > 4 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        zIndex: 10
                                    }}>
                                        +{imagesToDisplay.length - 4}
                                    </div>
                                )}
                            </div>
                        )}
                    </Flex>
                </Paper>
            )}

            <Fieldset mt="md" legend="Event Details" style={{ position: 'relative' }}>
                <FocusTrap>
                    <>
                        <Text fw={500}>Date:</Text>
                        <Group gap="sm" grow={false}>
                            {format === DateFormat.MONTH ? (
                                <MonthPickerInput
                                    size="md"
                                    placeholder="Pick month"
                                    value={date}
                                    onChange={(newDate) => {
                                        setDate(newDate);
                                        onEventUpdate({ date: newDate || undefined });
                                    }}
                                    w="100%"
                                    style={{ flex: 1 }}
                                />
                            ) : (
                                <DatePickerInput
                                    size="md"
                                    placeholder="Pick date"
                                    value={date}
                                    onChange={(newDate) => {
                                        setDate(newDate);
                                        onEventUpdate({ date: newDate || undefined });
                                    }}
                                    w="100%"
                                    style={{ flex: 1 }}
                                />
                            )}
                            <DateFormatControl
                                dateFormat={format}
                                onDateFormatChange={(newFormat) => {
                                    setFormat(newFormat);
                                    onEventUpdate({ dateFormat: newFormat });
                                }}
                            />
                        </Group>

                        <TextInput
                            mt="md"
                            size="md"
                            label="Description:"
                            value={description}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                            onBlur={() => onEventUpdate({ title: description })}
                        />

                        {/* Location description field */}
                        <TextInput
                            mt="md"
                            size="md"
                            label="Location:"
                            placeholder="e.g. Home, School, Grandma's Cabin"
                            description="A general or familiar name for this place"
                            value={locationDescription}
                            onChange={(e) => setLocationDescription(e.currentTarget.value)}
                            onBlur={() => onEventUpdate({ location_description: locationDescription })}
                        />

                        {/* Address field with Google Places Autocomplete */}
                        <Box mt="md">
                            <Text fw={500} size="md" mb={0}>Address:</Text>
                            <Text size="sm" color="dimmed" mb={5}>Search for a specific address or city to geotag your image</Text>
                            <Box style={{ position: 'relative' }}>
                                {/* Container for Google Places Autocomplete */}
                                <div
                                    ref={locationSearchContainerRef}
                                    style={{
                                        width: '100%',
                                        minHeight: '36px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                    }}
                                />

                                {/* Show loading indicator while Google Maps is loading */}
                                {isGoogleMapsLoading && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)'
                                    }}>
                                        <IconLoader2 size={18} className="mantine-spin" />
                                    </div>
                                )}

                                {/* Show current selected place address if set */}
                                {selectedPlaceAddress && !isGoogleMapsLoading && (
                                    <Group mt={5} gap="xs">
                                        <IconMapPin size={14} />
                                        <Text size="sm" style={{ fontStyle: 'italic' }}>{selectedPlaceAddress}</Text>
                                    </Group>
                                )}
                            </Box>
                        </Box>

                        <TagsInput
                            size="md"
                            mt="md"
                            label="Keyword Tags:"
                            placeholder="Enter tag"
                            value={tags}
                            onChange={(newTags) => {setTags(newTags); onEventUpdate({ keywords: newTags });}}
                            clearable
                        />
                    </>
                </FocusTrap>
            </Fieldset>


        </>
    );
};